import type { Identifier } from "ra-core";

/** A row of gula_materials: a file the setters send by WhatsApp. */
export interface Material {
  id: Identifier;
  title: string;
  category: MaterialCategory;
  kind: MaterialKind;
  storage_path: string;
  url: string;
  file_name: string;
  mime_type?: string | null;
  size_bytes?: number | null;
  note?: string | null;
  caption?: string | null;
  sort: number;
  active: boolean;
  created_at: string;
}

export type MaterialKind = "image" | "video" | "document";
export type MaterialCategory =
  | "videos"
  | "payment"
  | "prices"
  | "technical"
  | "photos"
  | "other";

export const MATERIAL_CATEGORIES: { value: MaterialCategory; label: string }[] =
  [
    { value: "videos", label: "Vídeos" },
    { value: "payment", label: "Cómo pagar" },
    { value: "prices", label: "Precios y financiación" },
    { value: "technical", label: "Ficha técnica y catálogo" },
    { value: "photos", label: "Fotos de grifos" },
    { value: "other", label: "Otros" },
  ];

/** WhatsApp limits per type (Cloud API): 5 MB images, 16 MB videos, 100 MB documents. */
const LIMITS: Record<MaterialKind, number> = {
  image: 5 * 1024 * 1024,
  video: 16 * 1024 * 1024,
  document: 50 * 1024 * 1024, // our bucket limit
};

/** How WhatsApp will send a file: JPG/PNG as photo, MP4 as video, the rest as a document. */
export const kindForFile = (mimeType: string): MaterialKind => {
  if (mimeType === "image/jpeg" || mimeType === "image/png") return "image";
  if (mimeType === "video/mp4") return "video";
  return "document";
};

/** Why a file cannot go to WhatsApp, or null when it can. */
export const fileProblem = (file: { type: string; size: number }) => {
  const kind = kindForFile(file.type);
  if (file.type.startsWith("video/") && kind !== "video")
    return "WhatsApp solo acepta vídeos MP4";
  if (file.size > LIMITS[kind])
    return `Pesa demasiado para WhatsApp (máximo ${LIMITS[kind] / 1024 / 1024} MB)`;
  return null;
};

/** The title a file gets by default: its name without extension or brand prefix. */
export const titleFromFileName = (fileName: string) =>
  fileName
    .replace(/\.[^.]+$/, "")
    .replace(/^gula de agua\s*-\s*/i, "")
    .trim();

/** Storage key: ASCII, no spaces, unique. */
export const storagePathFor = (fileName: string, unique: string) => {
  const extension = fileName.includes(".")
    ? fileName.split(".").pop()!.toLowerCase()
    : "bin";
  const base = fileName
    .replace(/\.[^.]+$/, "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);
  return `${unique}-${base || "archivo"}.${extension}`;
};

/** Active materials grouped by category, in the category order, then by sort. */
export const groupMaterials = (materials: Material[]) =>
  MATERIAL_CATEGORIES.map((category) => ({
    ...category,
    items: materials
      .filter((item) => item.active && item.category === category.value)
      .sort((a, b) => a.sort - b.sort || a.title.localeCompare(b.title)),
  })).filter((group) => group.items.length);

export const formatSize = (bytes?: number | null) => {
  if (!bytes) return "";
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1).replace(".", ",")} MB`;
};
